<template>
  <DataTable
      scrollable
      scroll-height="flex"
      :value="devicesRecentlyViewedPageStore.getDevices"
      lazy
      paginator
      :first="devicesRecentlyViewedPageStore.getFirstPage"
      :rows="devicesRecentlyViewedPageStore.params.per_page"
      :rowsPerPageOptions="devicesRecentlyViewedPageStore.pages"
      :total-records="devicesRecentlyViewedPageStore.totalRecords"
      :loading="devicesRecentlyViewedPageStore.loading"
      @page="devicesRecentlyViewedPageStore.onPage($event)"
      @update:rows="devicesRecentlyViewedPageStore.onRowsUpdate"
      data-key="id"
      size="small"
      always-show-paginator
  >
    <template #header>
      <Toolbar class="border-none shadow-none -m-3.5 rounded-none">
        <template #start>
          <Button v-if="$auth.hasPermission('create:devices')" :label="$t('common.create')"
                  class="mr-2"
                  @click="createDevice"
                  data-cy="create-button">
            <template #icon="slotProps">
              <IconPlus class="w-4 h-4 mr-1" :class="slotProps.class"/>
            </template>
          </Button>
        </template>

        <template #end>
          <div class="flex items-stretch justify-end space-x-2">
            <div class="flex-1 divide-x divide-slate-300 grid grid-cols-2">
              <IndexHeaderStat
                  :title="$t('pages.devices.devices_recently_viewed_view.devices_total')"
                  :value="devicesDashboardPageStore?.getDevicesTotalCount"
                  :loading="devicesDashboardPageStore?.getDevicesTotalCountLoading"
              />
              <IndexHeaderStat
                  :title="$t('pages.devices.devices_recently_viewed_view.needing_an_update')"
                  :value="devicesDashboardPageStore?.getDevicesNeedingAnUpdateCount"
                  :loading="devicesDashboardPageStore?.getDevicesNeedingAnUpdateCountLoading"
              />
            </div>
            <Button text severity="secondary" @click="refreshStats(true)">
              <template #icon="{class: iconClass}">
                <IconRefresh :class="iconClass" class="w-5 h-5"/>
              </template>
            </Button>
          </div>
        </template>
      </Toolbar>
    </template>
    <Column field="id" :header="$t('common.id')" class="pl-6 lg:pl-8 w-12"/>

    <Column header="&nbsp;" class="w-[6rem]">
      <template #body="{data}">
        <div class="h-20 aspect-square flex items-center justify-center">
          <img :src="getImageUrl(data)" alt=""/>
        </div>
      </template>
    </Column>

    <Column field="serial_number" class="whitespace-nowrap">
      <template #header="{data}">
        <span class="px-2.5 py-1.5">{{ $t('common.serial_number') }}</span>
      </template>
      <template #body="{data}">
        <router-link :to="{ name: 'devices.show', params: { device: data.id } }">
          <Button type="button" :label="data.serial_number" text/>
        </router-link>
      </template>
    </Column>

    <Column field="last_viewed_at" :header="$t('common.last_viewed')" class="w-44 whitespace-nowrap">
      <template #body="{data}">
        {{ formatDate(data.last_viewed_at) }}
      </template>
    </Column>

    <Column field="company.name" :header="$t('common.company')" class="whitespace-nowrap">
      <template #body="{data}">
        {{ data.company?.name || '-' }}
      </template>
    </Column>

    <Column field="device_model.name" :header="$t('common.model')" class="whitespace-nowrap">
      <template #body="{data}">
        {{ data.device_model?.name || '-' }}
      </template>
    </Column>

    <Column field="device_type.name" :header="$t('common.type')" class="whitespace-nowrap">
      <template #body="{data}">
        {{ data.device_type?.name || '-' }}
      </template>
    </Column>

    <Column field="device_revision.name" :header="$t('common.revision')">
      <template #body="{data}">
        {{ data.device_revision?.name || '-' }}
      </template>
    </Column>

    <Column field="current_firmware.version_number" :header="$t('common.firmware_version')">
      <template #body="{data}">
        {{ data.current_firmware?.version_number || '-' }}
      </template>
    </Column>

    <Column field="requires_update" :header="$t('common.requires_update')" style="min-width: 12rem">
      <template #body="{ data }">
        <Tag :value="getUpdateStatusName(data.requires_update)" :severity="getUpdateStatus(data.requires_update)"/>
      </template>
    </Column>
  </DataTable>

  <DeviceSlideOver ref="slideOver" @saved="devicesRecentlyViewedPageStore.loadData"/>
</template>

<script>
import {useDevicesRecentlyViewedPageStore} from "@/stores/pages/devicesRecentlyViewedPage.js";
import {useDevicesDashboardPageStore} from "@/stores/pages/devicesDashboardPage.js";
import {mapStores} from "pinia";
import DeviceSlideOver from "@/Slideovers/DeviceSlideOver.vue";
import {IconPlus, IconRefresh} from "@tabler/icons-vue";
import IndexHeaderStat from "@/Widgets/Cards/IndexHeaderStat.vue";
import dayjs from "dayjs";
import {resolveDeviceImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "DevicesRecentlyViewedView",
  computed: {
    ...mapStores(useDevicesRecentlyViewedPageStore),
    ...mapStores(useDevicesDashboardPageStore),
    requiresUpdateStatuses() {
      return [
        {name: this.$t('common.requires_update'), value: 1},
        {name: this.$t('pages.devices.devices_recently_viewed_view.updated'), value: 0},
      ];
    },
  },
  components: {
    IndexHeaderStat,
    IconPlus,
    DeviceSlideOver,
    IconRefresh
  },
  methods: {
    createDevice() {
      this.$refs.slideOver.createDevice();
    },
    getUpdateStatusName(status) {
      const found = this.requiresUpdateStatuses.filter(requiresUpdate => requiresUpdate.value === status);
      return found.length > 0 ? found[0].name : this.$t('pages.devices.devices_recently_viewed_view.unknown');
    },
    getUpdateStatus(status) {
      switch (status) {
        case 1:
          return 'danger';
        case 0:
          return 'success';
      }
      return null;
    },
    getImageUrl(device) {
      return resolveDeviceImageUrl(device, 'https://placehold.co/600x400')
    },
    refreshStats(forceRefresh = false) {
      this.devicesDashboardPageStore.fetchDevicesTotalCount({}, forceRefresh);
      this.devicesDashboardPageStore.fetchDevicesNeedingAnUpdate({}, forceRefresh);
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-';
    }
  },
  async mounted() {
    try {
      await this.devicesRecentlyViewedPageStore.loadData();
    } catch (error) {
      console.error('Failed to load recently viewed devices:', error);
    } finally {
      this.refreshStats();
    }
  }
}
</script>
